import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import {
  RichTreeView,
  TreeViewBaseItem,
} from "@mui/x-tree-view";
import { v4 as uuidv4 } from "uuid";
import { Product, ProductType, AdjustedProductType } from "../../../interfaces/DTO/mislaka_xml_models";
import { formatCurrency, formatPercentageRound } from "../../../utils/namesFormater";
import { FundDataBasic } from "../../../interfaces/DTO/FundDataBasic";
import { styled } from "@mui/material/styles";
import { formatAdjustedProductType } from "../../../utils/enumFormatter";
import { MimshakService } from "../../../services/mimshakService";
import { getBucket } from "../../../utils/fundsFormatter";
import { buildTreeItemLabelString, CustomCheckbox, CustomTreeItem } from "../../../shared/UIParts/CustomTreeItem";
import { colorAdjustedProductType } from "../../../utils/colorEnumFormatter";

interface InvestmentPathTreeProps {
  products: Product[];
  fundData?: FundDataBasic[];
}

type InvestmentRow = {
  policy_number: string;
  product_type: ProductType;
  adjusted_product_type?: AdjustedProductType;
  sub_specialization: string;
  accumulated_amount: number;
  stock_market_bucket: string;
  path_name: string;
  path_code: number;
  stock_market_percent?: number;
  color?: string;
};

const groupBy = <T,>(
  array: T[],
  key: keyof T | ((item: T) => string)
): Record<string, T[]> => {
  return array.reduce((acc, item) => {
    const groupKey =
      typeof key === "function"
        ? key(item)
        : String(item[key] ?? "לא ידוע");
    if (!acc[groupKey]) {
      acc[groupKey] = [];
    }
    acc[groupKey].push(item);
    return acc;
  }, {} as Record<string, T[]>);
};

const sum = (nums: number[]) => nums.reduce((a, b) => a + (b || 0), 0);

// Helper function to extract the minimum value from bucket string for sorting
const getBucketSortValue = (bucket: string): number => {
  const match = bucket.match(/^(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
};

type HierarchyMode = "sub_specializationFirst" | "productTypeFirst";

const generateTreeData = (
  rows: InvestmentRow[],
  mode: HierarchyMode
): TreeViewBaseItem[] => {
  if (mode === "sub_specializationFirst") {
    const groupedBySubSpec = groupBy(rows, (r) => `${r.sub_specialization} | ${r.stock_market_bucket}`);

    // ✅ מיון לפי טווח חשיפה למניות (סדר יורד)
    const sortedEntries = Object.entries(groupedBySubSpec).sort(([keyA], [keyB]) => {
      const bucketA = keyA.split(" | ")[1];
      const bucketB = keyB.split(" | ")[1];
      return getBucketSortValue(bucketB) - getBucketSortValue(bucketA);
    });

    return sortedEntries.map(([combinedKey, subRows]) => {
      const totalSub = sum(subRows.map((r) => r.accumulated_amount));
      const [subSpec, bucket] = combinedKey.split(" | ");
      // ✅ קיבוץ לפי adjusted_product_type אם קיים, אחרת product_type
      const groupedByProductType = groupBy(subRows, (r) =>
        String(r.adjusted_product_type ?? r.product_type)
      );

      return {
        id: uuidv4(),
        label: buildTreeItemLabelString({
            primary: subSpec,
            secondary: formatCurrency(totalSub),
            chip: bucket,
          }),
        children: Object.entries(groupedByProductType).map(
          ([prodType, prodRows]) => {
            const totalProd = sum(prodRows.map((r) => r.accumulated_amount));
            const typeNum = Number(prodType);
            return {
              id: uuidv4(),
              label: buildTreeItemLabelString({
                primary: formatAdjustedProductType(typeNum),
                secondary: formatCurrency(totalProd),
                colorHex: colorAdjustedProductType(typeNum),
              }),
              children: prodRows.map((row) => {
                const rowTypeNum = row.adjusted_product_type ?? row.product_type;
                return {
                  id: uuidv4(),
                  label: buildTreeItemLabelString({
                      primary: row.path_name,
                      secondary: formatCurrency(row.accumulated_amount),
                      chip: formatPercentageRound(row.stock_market_percent!) || "⚠️",
                      grayText: row.policy_number,
                      colorHex: colorAdjustedProductType(rowTypeNum as AdjustedProductType),
                    }),
                };
              }),
            };
          }
        ),
      };
    });
  } else {
    // ✅ קיבוץ לפי adjusted_product_type אם קיים, אחרת product_type
    const groupedByProductType = groupBy(rows, (r) =>
      String(r.adjusted_product_type ?? r.product_type)
    );
    return Object.entries(groupedByProductType).map(([prodType, prodRows]) => {
      const totalProd = sum(prodRows.map((r) => r.accumulated_amount));
      const groupedBySubSpec = groupBy(prodRows, (r) => `${r.sub_specialization} | ${r.stock_market_bucket}`);
      const typeNum = Number(prodType);

      return {
        id: uuidv4(),
        label: buildTreeItemLabelString({
            primary: formatAdjustedProductType(typeNum),
            secondary: formatCurrency(totalProd),
            colorHex: colorAdjustedProductType(typeNum),
          }),
        children: Object.entries(groupedBySubSpec).map(([combinedKey, subRows]) => {
          const totalSub = sum(subRows.map((r) => r.accumulated_amount));
          const [subSpec, bucket] = combinedKey.split(" | ");

          return {
            id: uuidv4(),
            label: buildTreeItemLabelString({
                primary: subSpec,
                secondary: formatCurrency(totalSub),
                chip: bucket,
              }),
            children: subRows.map((row) => {
              const rowTypeNum = row.adjusted_product_type ?? row.product_type;
              return {
                id: uuidv4(),
                label: buildTreeItemLabelString({
                  primary: row.path_name,
                  secondary: formatCurrency(row.accumulated_amount),
                  chip: formatPercentageRound(row.stock_market_percent!) || "⚠️",
                  grayText: row.policy_number,
                  colorHex: colorAdjustedProductType(rowTypeNum as AdjustedProductType),
                }),
              };
            }),
          };
        }),
      };
    });
  }
};


export const InvestmentPathTree: React.FC<InvestmentPathTreeProps> = ({
  products,
  fundData,
}) => {
  const [hierarchyMode, setHierarchyMode] =
    useState<HierarchyMode>("sub_specializationFirst");
  const validProducts = useMemo(() => {
    return products.filter(product =>
      product.policies_or_accounts.some(policy =>
        MimshakService.mergeDuplicateInvestmentPaths(policy.investment_paths).some(path =>
          fundData?.some(f => f.fund_id === path.path_code)
        )
      )
    );
  }, [products, fundData]);
  const investmentRows: InvestmentRow[] = useMemo(() => {
    return products.flatMap((product) =>
      product.policies_or_accounts.flatMap((policy) => {
        const mergedPaths = MimshakService.mergeDuplicateInvestmentPathsByPathCode(policy.investment_paths);

        return mergedPaths.map((path) => {
          const fund = fundData?.find((f) => f.fund_id === path.path_code);
          if (!fund) return null;
          return {
            policy_number: policy.policy_number,
            product_type: product.details.product_type,
            adjusted_product_type: product.details.adjusted_product_type,
            sub_specialization: fund?.sub_specialization ?? "לא ידוע",
            accumulated_amount: path.accumulated_amount ?? 0,
            stock_market_bucket: getBucket(fund?.stock_market_percent),
            stock_market_percent: fund?.stock_market_percent,
            path_name: path.path_name,
            path_code: path.path_code,
          };
        }).filter(Boolean); // ✅ מסנן את ה-nullים;
      })
    );
  }, [validProducts, fundData]);

  const treeData = useMemo(
    () => generateTreeData(investmentRows, hierarchyMode),
    [investmentRows, hierarchyMode]
  );

  return (
    <Box sx={{ p: 2, maxHeight: "100%", overflow: "auto" }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="h6">עץ השקעות</Typography>

        <Stack direction="row" spacing={0} alignItems="center">
          <Button
            variant={
              hierarchyMode === "sub_specializationFirst"
                ? "contained"
                : "outlined"
            }
            onClick={() => setHierarchyMode("sub_specializationFirst")}
          >
            → תחום השקעה
          </Button>
          <Divider orientation="vertical" flexItem />
          <Button
            variant={
              hierarchyMode === "productTypeFirst" ? "contained" : "outlined"
            }
            onClick={() => setHierarchyMode("productTypeFirst")}
          >
            סוג מוצר →
          </Button>
        </Stack>
      </Box>

      <RichTreeView
        items={treeData}
        getItemId={(item) => item.id}
        getItemLabel={(item) => item.label}
        slots={{
          item: (props: any) => <CustomTreeItem {...props} hideCheckbox />,
        }}
      />
    </Box>
  );
};
